import React, { createContext, useContext, useEffect, useState } from 'react'
import { STATUS } from '../../types/audit'
import { fetchData } from '../../utils/fetch'
import { getAuditConfigs } from './api'

// Flat audit configuration structure
export interface AuditConfig {
  SECTIONS: Record<string, string>
  SECTIONS_ORDER: Record<string, string>
  TITLES: Record<string, string>
  QUESTIONS: Record<string, string>
  IMAGE_TYPES: Record<string, string[]>
  DEFECTS: Record<string, string[]>
  VAN_DATA_MAPPINGS?: Record<string, string> // Weekly DVIR only
}

interface IContextProps {
  configs: Record<STATUS, AuditConfig>
  loading: boolean
  error: string | null
}

interface IProviderProps {
  children: React.ReactNode
}

export const AuditConfigContext = createContext<IContextProps>({
  configs: {} as Record<STATUS, AuditConfig>,
  loading: true,
  error: null,
})

export function AuditConfigProvider({ children }: IProviderProps) {
  const [configs, setConfigs] = useState<Record<STATUS, AuditConfig>>(
    {} as Record<STATUS, AuditConfig>,
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadConfigs() {
      try {
        const data = await fetchData<Record<STATUS, AuditConfig>>(
          getAuditConfigs(),
        )
        setConfigs(data)
        setLoading(false)
      } catch (err) {
        console.error('Failed to load audit configs:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
        setLoading(false)
      }
    }

    loadConfigs()
  }, [])

  return (
    <AuditConfigContext.Provider value={{ configs, loading, error }}>
      {children}
    </AuditConfigContext.Provider>
  )
}

/**
 * Hook to access audit configuration for a specific audit type
 * @param auditType - The audit status/type (DAILY_DVIR, WEEKLY_DVIR, etc.)
 * @returns The flat config for the specified audit type
 * @throws Error if configs are still loading or failed to load
 */
export function useAuditConfig(auditType: STATUS): AuditConfig {
  const { configs, loading, error } = useContext(AuditConfigContext)

  if (loading) {
    throw new Error('Audit configs are still loading')
  }

  if (error) {
    throw new Error(`Failed to load audit configs: ${error}`)
  }

  if (!configs[auditType]) {
    throw new Error(`No config found for audit type: ${auditType}`)
  }

  return configs[auditType]
}
